import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight, ChatCircleText, Flag, Scales, Trash } from "@phosphor-icons/react";
import { useSeo, useSite } from "@/hooks/useSite";
import ContactForm from "@/components/site/ContactForm";
import AppealFlow from "@/components/site/AppealFlow";
import DeleteFlow from "@/components/site/DeleteFlow";
import { IconTile, PageHero, SitePage } from "@/components/site/SiteBits";

const find = (doc, re) => doc?.sections?.find((s) => re.test(s.heading || ""));
const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Card({ id, icon, title, body, children, testId }) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="s-card scroll-mt-24 p-7 sm:p-8" data-testid={testId}>
      <div className="flex items-start gap-4">{icon && <IconTile icon={icon} />}
        <div><h2 id={`${id}-title`} className="text-xl font-bold tracking-tight">{title}</h2>{body && <p className="s-muted mt-2 leading-7" data-testid={`${testId}-copy`}>{body}</p>}</div></div>
      {children && <div className="mt-6">{children}</div>}
    </section>
  );
}

// Text: site_support fields; where a field isn't published yet, the older support_copy sections (also Content).
export default function Support() {
  const { data, error, loading, reload } = useSite();
  const f = data?.docs?.site_support?.fields || {};
  const old = data?.docs?.support_copy;
  const here = old?.sections?.[0], appeal = find(old, /appeal/i), del = find(old, /delete/i);
  const faq = data?.docs?.faq?.groups?.[0]?.items?.slice(0, 4) || [];
  const [topic, setTopic] = useState(null);
  const formRef = useRef(null);
  useSeo("Support", f.hero_lead || here?.body);
  const report = () => { setTopic({ value: "problem" }); go("contact"); setTimeout(() => formRef.current?.querySelector("textarea")?.focus({ preventScroll: true }), 450); };
  const quick = [["contact", f.btn_contact, () => go("contact")], ["appeal", f.btn_appeal, () => go("appeal")], ["delete", f.btn_delete, () => go("delete")], ["report", f.btn_report, report]];

  return (
    <SitePage loading={loading} error={error} onRetry={reload} testId="support-page">
      <PageHero chip={f.hero_chip || old?.title} title={f.hero_title || here?.heading} accent={f.hero_accent} lead={f.hero_lead || here?.body} testId="support-hero">
        <div className="mt-8 flex flex-wrap gap-2" data-testid="support-quick">
          {quick.filter(([, l]) => l).map(([k, l, fn]) => <button key={k} type="button" onClick={fn} className="s-btn s-btn-ghost s-focus" data-testid={`support-quick-${k}`}>{l}</button>)}
        </div>
      </PageHero>
      <div className="mt-14 grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <Card id="contact" icon={ChatCircleText} title={f.contact_title || f.btn_contact} body={f.contact_sub || here?.body} testId="support-contact">
            <ContactForm max={data?.limits?.contact_max_chars} topic={topic} formRef={formRef} copy={f} />
          </Card>
          {f.report_title && <Card id="report" icon={Flag} title={f.report_title} body={f.report_body} testId="support-report" />}
        </div>
        <div className="space-y-6">
          {faq.length > 0 && (
            <section className="s-card p-7" aria-labelledby="sup-faq" data-testid="support-faq-links">
              <h2 id="sup-faq" className="text-xl font-bold">{f.common_title}</h2>
              <ul className="mt-4 divide-y s-line divide-[var(--s-line)]">
                {faq.map((it, i) => <li key={i}><Link to="/faq" className="s-focus flex items-center justify-between gap-3 rounded py-3.5 text-[0.9375rem] hover:text-[var(--s-coral-2)]" data-testid={`support-faq-link-${i}`}>{it.q}<CaretRight size={14} aria-hidden /></Link></li>)}
              </ul>
              <Link to="/faq" className="s-coral s-focus mt-3 inline-flex items-center gap-1 rounded text-sm font-semibold" data-testid="support-all-faq-link">{f.common_all || f.common_link}<CaretRight size={12} aria-hidden /></Link>
            </section>)}
          <Card id="appeal" icon={Scales} title={f.appeal_title || appeal?.heading} body={f.appeal_body || appeal?.body} testId="support-appeal">
            <AppealFlow phoneLabel={f.appeal_phone_label} buttonLabel={f.appeal_button} />
          </Card>
          <Card id="delete" icon={Trash} title={f.delete_title || del?.heading} body={f.delete_body || del?.body} testId="support-delete">
            <DeleteFlow phoneLabel={f.delete_phone_label} buttonLabel={f.delete_button} />
          </Card>
        </div>
      </div>
    </SitePage>
  );
}
